import TextField from '@mui/material/TextField';
import { type ChangeEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../../context/FiltersContext';
import type { TextInputConfigurableFilter } from '../../types';

export type TextInputSelectorProps = {
  filterKey: string;
  selectedOptions?: string;
  onOptionChange: (value: string) => void;
  setValidationState?: (value: boolean) => void;
} & Pick<TextInputConfigurableFilter<unknown>, 'config'>;

export const TextInputSelector = ({
  filterKey,
  selectedOptions,
  onOptionChange,
  config,
  setValidationState,
}: TextInputSelectorProps) => {
  const { t } = useTranslation();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { filtersErrors, setFiltersErrors } = useFiltersContext();
  const [filterVal, setFilterVal] = useState<string>(selectedOptions ?? '');

  const { fieldPlaceholder, startAdornment, endAdornment, customRegex, debounce } = config ?? {};

  const validateValue = useCallback(
    (val: string, isOnChange: boolean) => {
      const regex = customRegex || /.*/;
      const isValid = val === '' || regex.test(val);

      if (!isValid) {
        setFiltersErrors(prev => ({
          ...prev,
          [filterKey]: t('Invalid value'),
        }));
        setValidationState?.(true);
      } else {
        setFiltersErrors(prev => {
          const { [filterKey]: _, ...rest } = prev;
          return rest;
        });
        setValidationState?.(false);

        if (isOnChange) {
          const trimmed = val.trim();

          if (typeof debounce === 'number') {
            if (debounceRef.current !== null) {
              clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => onOptionChange(trimmed), debounce);
          } else {
            onOptionChange(trimmed);
          }
        }
      }
    },
    [customRegex, filterKey, setFiltersErrors, setValidationState, t, onOptionChange, debounce]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (filterVal !== '') {
      validateValue(filterVal, false);
    }
  }, [validateValue, filterVal]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      const val = e.target.value;
      setFilterVal(val);
      validateValue(val, true);
    },
    [validateValue]
  );

  return (
    <TextField
      data-test="text-filter-input"
      variant="outlined"
      size="small"
      value={filterVal}
      onChange={handleChange}
      placeholder={fieldPlaceholder ?? t('Enter value')}
      slotProps={{
        htmlInput: {
          type: 'text',
          sx: {
            marginLeft: 0.5,
            marginRight: 0.5,
          },
        },
        input: {
          startAdornment,
          endAdornment,
        },
      }}
      sx={{ width: '100%' }}
      error={!!filtersErrors[filterKey]}
      helperText={filtersErrors[filterKey]}
    />
  );
};
